import { motion } from 'motion/react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

import type { EditableSectionProps } from '@/types/theme';

const fadeUp = (delay = 0) => ({
	hidden: { opacity: 0, y: 22 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.72, delay, ease: [0.16, 1, 0.3, 1] as const } },
});

export function HeroShowcase({ title, description, primaryCta, secondaryCta, image }: EditableSectionProps) {
	return (
		<section className="relative overflow-hidden bg-background">
			{/* Top accent — gradient gold line */}
			<div
				aria-hidden="true"
				className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/80 to-transparent"
			/>

			{/* Atmospheric warm glow anchored behind the headline */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute left-[18%] top-[30%] h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/[0.055] blur-[140px]"
			/>

			<div className="mx-auto grid min-h-[88vh] w-full max-w-none lg:grid-cols-[1.1fr_0.9fr]">

				{/* ── Left: copy ─────────────────────────────────── */}
				<div className="relative flex flex-col justify-center px-6 py-24 md:px-12 lg:px-16 xl:px-24">

					{/* Architectural left-rail */}
					<div
						aria-hidden="true"
						className="absolute bottom-0 left-6 top-0 hidden w-px bg-gradient-to-b from-transparent via-border/70 to-transparent md:left-12 md:block lg:left-16 xl:left-24"
					/>

					<motion.div
						initial="hidden"
						animate="visible"
						variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
					>
						{/* Eyebrow */}
						<motion.div variants={fadeUp(0)} className="flex items-center gap-3">
							<div className="h-px w-7 shrink-0 bg-accent/70" aria-hidden="true" />
							<p className="text-[10px] font-semibold uppercase tracking-[0.44em] text-accent/75">
								Gurdwara Shahidan Char Sahibzade
							</p>
						</motion.div>

						{/* Headline */}
						<motion.h1
							variants={fadeUp(0.04)}
							className="mt-7 font-serif text-5xl leading-[0.92] tracking-tight text-foreground sm:text-6xl md:text-7xl xl:text-[5.5rem]"
						>
							{title ? (
								title
							) : (
								<>
									Prayer,<br />
									remembrance,<br />
									<em className="not-italic text-accent">community.</em>
								</>
							)}
						</motion.h1>

						{/* Rule */}
						<motion.div
							variants={fadeUp(0.1)}
							className="mt-9 flex items-center gap-4"
						>
							<div className="h-px w-10 shrink-0 bg-foreground/15" aria-hidden="true" />
							<p className="text-[14.5px] leading-[1.9] text-foreground/45">
								{description || 'A quiet digital home for Gurdwara Shahidan Char Sahibzade — open, respectful, and built to serve the sangat.'}
							</p>
						</motion.div>

						{/* CTAs */}
						<motion.div variants={fadeUp(0.16)} className="mt-10 flex flex-wrap items-center gap-5">
							{/* Primary */}
							<a
								href={primaryCta?.url || '/programs'}
								className={[
									'group relative inline-flex h-11 items-center gap-2.5 overflow-hidden rounded-[0.65rem]',
									'bg-accent px-6 text-[13px] font-semibold tracking-[0.01em] text-accent-foreground',
									'shadow-[0_1px_0_rgba(255,255,255,0.13)_inset,0_2px_14px_rgba(184,154,50,0.38)]',
									'transition-all duration-300',
									'hover:brightness-105 hover:shadow-[0_1px_0_rgba(255,255,255,0.13)_inset,0_4px_24px_rgba(184,154,50,0.52)]',
								].join(' ')}
							>
								{/* inner top highlight */}
								<span
									aria-hidden="true"
									className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
								/>
								{primaryCta?.label || 'Explore programs'}
								<ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
							</a>

							{/* Secondary — minimal text link */}
							<a
								href={secondaryCta?.url || '/visit'}
								className="group inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground/45 transition-colors duration-200 hover:text-foreground"
							>
								{secondaryCta?.label || 'Plan a visit'}
								<ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
							</a>
						</motion.div>
					</motion.div>
				</div>

				{/* ── Right: image ───────────────────────────────── */}
				<motion.div
					initial={{ opacity: 0, scale: 1.025 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 1.05, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
					className="relative min-h-[55vh] lg:min-h-full"
				>
					{/* left divider */}
					<div
						aria-hidden="true"
						className="absolute inset-y-0 left-0 hidden w-px bg-border/60 lg:block"
					/>

					{image?.url ? (
						<>
							<img
								src={image.url}
								alt={image.alt || title || 'Hero image'}
								className="absolute inset-0 h-full w-full object-cover"
							/>
							{/* Left-edge dissolve into copy */}
							<div
								aria-hidden="true"
								className="absolute inset-y-0 left-0 hidden w-28 bg-gradient-to-r from-background to-transparent lg:block"
							/>
							{/* Bottom vignette */}
							<div
								aria-hidden="true"
								className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-background/20 to-transparent"
							/>
						</>
					) : (
						<div className="absolute inset-0 bg-[#CACACA]">
							{/* Refined architectural placeholder */}
							<div className="absolute inset-0 flex items-center justify-center">
								<div className="relative h-[68%] w-[50%]">
									{/* Vertical axis */}
									<div className="absolute bottom-0 left-1/2 h-[88%] w-px -translate-x-1/2 bg-foreground/12" />
									{/* Top rectangle */}
									<div className="absolute left-1/2 top-[5%] h-[28%] w-[46%] -translate-x-1/2 border border-foreground/10 bg-foreground/[0.03]" />
									{/* Central pillar */}
									<div className="absolute left-1/2 top-[34%] h-[50%] w-[13%] -translate-x-1/2 bg-foreground/20" />
									{/* Base line */}
									<div className="absolute bottom-0 left-[16%] h-px w-[68%] bg-foreground/10" />
									{/* Gold accent dot */}
									<div className="absolute left-1/2 top-[33%] h-[5px] w-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/80 shadow-[0_0_8px_rgba(184,154,50,0.5)]" />
								</div>
							</div>
							{/* Label */}
							<p className="absolute bottom-5 right-5 text-[9.5px] font-semibold uppercase tracking-[0.44em] text-foreground/25">
								Add image in editor
							</p>
						</div>
					)}
				</motion.div>
			</div>

			{/* Bottom border */}
			<div className="absolute inset-x-0 bottom-0 h-px bg-border" aria-hidden="true" />
		</section>
	);
}
